import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { getUser, shouldShowManageTenants, shouldShowMerchants, shouldShowTenantGroups } from 'Selectors';
import UpdateCount from './UpdateCount';
import styles from './styles.css';

type Props = {
    prevDeploymentSummary: DeploymentSummary;
    newDeploymentSummary: DeploymentSummary;
};

const DeployCount = ({ prevDeploymentSummary, newDeploymentSummary }: Props) => {
    const { t } = useTranslation();
    const { landlordId, tenantKey } = useSelector(getUser);
    const {
        addedRules: addedAcquirer,
        modifiedRules: modifiedAcquirer,
        configUpdated: configUpdatedAcquirer,
        removedRules: removedAcquirer
    } = _.get(prevDeploymentSummary, 'acquirerSummary');
    const {
        added: addedMerchant,
        modified: modifiedMerchant,
        removed: removedMerchant,
        disabled: disabledMerchant
    } = _.get(prevDeploymentSummary, 'merchantSummary');
    const { added: addedTenantGroup, modified: modifiedTenantGroup, removed: removedTenantGroup } = _.get(
        prevDeploymentSummary,
        'tenantGroupSummary'
    );
    const { added: addedParameters, removed: removedParameters } = _.get(prevDeploymentSummary, 'parameterSummary');

    const {
        addedRules: newAddedAcquirer,
        modifiedRules: newModifiedAcquirer,
        removedRules: newRemovedAcquirer
    } = _.get(newDeploymentSummary, 'acquirerSummary');
    const {
        added: newAddedMerchant,
        modified: newModifiedMerchant,
        removed: newRemovedMerchant,
        disabled: newDisabledMerchant
    } = _.get(newDeploymentSummary, 'merchantSummary');
    const { added: newAddedTenantGroup, modified: newModifiedTenantGroup, removed: newRemovedTenantGroup } = _.get(
        newDeploymentSummary,
        'tenantGroupSummary'
    );
    const { added: newAddedParameters, removed: newRemovedParameters } = _.get(
        newDeploymentSummary,
        'parameterSummary'
    );

    const isShowMerchants = shouldShowMerchants();
    const isShowTenantGroup = shouldShowManageTenants(landlordId, tenantKey) && shouldShowTenantGroups();
    const acquirerModifiedTotal = modifiedAcquirer + removedAcquirer;
    const newAcquirerModifiedTotal = newModifiedAcquirer + newRemovedAcquirer;

    return (
        <ul className={styles.deployDetals}>
            {configUpdatedAcquirer && <li data-testid='global-overview-changed'>{t('Rules Overview updated')}</li>}

            {newAddedAcquirer ? (
                <li data-testid='global-added'>
                    {t('deploymentSummary.globalRules.newAdded', {
                        count: newAddedAcquirer
                    })}
                    <UpdateCount prevCount={addedAcquirer} newCount={newAddedAcquirer} />
                </li>
            ) : null}

            {newAcquirerModifiedTotal ? (
                <li data-testid='global-modified'>
                    {newRemovedAcquirer
                        ? t('deploymentSummary.globalRules.newModifiedAndRemoved', {
                              count: newAcquirerModifiedTotal,
                              removedOnlyCount: newRemovedAcquirer
                          })
                        : t('deploymentSummary.globalRules.newModified', {
                              count: newAcquirerModifiedTotal
                          })}
                    <UpdateCount prevCount={acquirerModifiedTotal} newCount={newAcquirerModifiedTotal} />
                </li>
            ) : null}

            {isShowMerchants && (
                <>
                    {newAddedMerchant ? (
                        <li data-testid='new-merchants'>
                            {t('deploymentSummary.merchantRules.newAdded', {
                                count: newAddedMerchant
                            })}
                            <UpdateCount prevCount={addedMerchant} newCount={newAddedMerchant} />
                        </li>
                    ) : null}

                    {newModifiedMerchant ? (
                        <li data-testid='merchants-modified'>
                            {t('deploymentSummary.merchantRules.newModified', {
                                count: newModifiedMerchant
                            })}
                            <UpdateCount prevCount={modifiedMerchant} newCount={newModifiedMerchant} />
                        </li>
                    ) : null}

                    {newRemovedMerchant ? (
                        <li data-testid='merchants-removed'>
                            {t('deploymentSummary.merchantRules.newRemoved', {
                                count: newRemovedMerchant
                            })}
                            <UpdateCount prevCount={removedMerchant} newCount={newRemovedMerchant} />
                        </li>
                    ) : null}

                    {newDisabledMerchant ? (
                        <li data-testid='merchants-deactivated'>
                            {t('deploymentSummary.merchantRules.newDisabled', {
                                count: newDisabledMerchant
                            })}
                            <UpdateCount prevCount={disabledMerchant} newCount={newDisabledMerchant} />
                        </li>
                    ) : null}
                </>
            )}

            {isShowTenantGroup && (
                <>
                    {newAddedTenantGroup ? (
                        <li data-testid='new-tenantOverrides'>
                            {t('deploymentSummary.tenantGroups.newAdded', {
                                count: newAddedTenantGroup
                            })}

                            <UpdateCount prevCount={addedTenantGroup} newCount={newAddedTenantGroup} />
                        </li>
                    ) : null}

                    {newModifiedTenantGroup || newRemovedTenantGroup ? (
                        <li data-testid='tenantOverrides-modified'>
                            {t('deploymentSummary.tenantGroups.newModified', {
                                count: newModifiedTenantGroup + newRemovedTenantGroup
                            })}

                            <UpdateCount
                                prevCount={modifiedTenantGroup + removedTenantGroup}
                                newCount={newModifiedTenantGroup + newRemovedTenantGroup}
                            />
                        </li>
                    ) : null}
                </>
            )}

            {newAddedParameters ? (
                <li data-testid='new-parameters'>
                    {t('deploymentSummary.parameters.newAdded', {
                        count: newAddedParameters
                    })}

                    <UpdateCount prevCount={addedParameters} newCount={newAddedParameters} />
                </li>
            ) : null}

            {newRemovedParameters ? (
                <li data-testid='parameters-removed'>
                    {t('deploymentSummary.parameters.newRemoved', {
                        count: newRemovedParameters
                    })}

                    <UpdateCount prevCount={removedParameters} newCount={newRemovedParameters} />
                </li>
            ) : null}
        </ul>
    );
};

export default React.memo(DeployCount);
